import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Wallet, Plus, Search, ArrowUpCircle, ArrowDownCircle, RefreshCw } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function WalletManagement() {
  const { user } = useAuth();
  const token = localStorage.getItem("sendtoo_token");
  const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };

  const [wallets, setWallets] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showTopup, setShowTopup] = useState(null);
  const [topupAmount, setTopupAmount] = useState("");
  const [topupDesc, setTopupDesc] = useState("Manual topup");
  const [selectedCustomer, setSelectedCustomer] = useState("");
  const [initBalance, setInitBalance] = useState("0");
  const [creditLimit, setCreditLimit] = useState("0");
  const [txns, setTxns] = useState([]);
  const [showTxns, setShowTxns] = useState(null);

  const fetchWallets = useCallback(async () => {
    setLoading(true);
    const r = await fetch(`${API}/api/v2/wallets`, { headers });
    if (r.ok) setWallets(await r.json());
    setLoading(false);
  }, []);

  const fetchCustomers = useCallback(async () => {
    const r = await fetch(`${API}/api/users?role=customer`, { headers });
    if (r.ok) setCustomers(await r.json());
  }, []);

  useEffect(() => { fetchWallets(); fetchCustomers(); }, [fetchWallets, fetchCustomers]);

  const createWallet = async () => {
    if (!selectedCustomer) { toast.error("Select a customer"); return; }
    const r = await fetch(`${API}/api/v2/wallets`, {
      method: "POST", headers,
      body: JSON.stringify({ customer_id: selectedCustomer, balance: parseFloat(initBalance) || 0, credit_limit: parseFloat(creditLimit) || 0 })
    });
    if (r.ok) { toast.success("Wallet created"); setShowCreate(false); fetchWallets(); }
    else { const e = await r.json(); toast.error(e.detail || "Failed"); }
  };

  const doTopup = async () => {
    const amt = parseFloat(topupAmount);
    if (!amt || amt <= 0) { toast.error("Enter valid amount"); return; }
    const r = await fetch(`${API}/api/v2/wallets/topup`, {
      method: "POST", headers,
      body: JSON.stringify({ customer_id: showTopup, amount: amt, description: topupDesc })
    });
    if (r.ok) { const d = await r.json(); toast.success(`Topped up. New balance: Rs.${d.new_balance}`); setShowTopup(null); setTopupAmount(""); fetchWallets(); }
    else { const e = await r.json(); toast.error(e.detail || "Failed"); }
  };

  const viewTxns = async (customerId) => {
    setShowTxns(customerId);
    const r = await fetch(`${API}/api/v2/wallets/${customerId}/transactions`, { headers });
    if (r.ok) setTxns(await r.json());
  };

  // Customers without wallets
  const walletsCustomerIds = wallets.map(w => w.customer_id);
  const availableCustomers = customers.filter(c => !walletsCustomerIds.includes(c.id));

  return (
    <DashboardLayout title="Admin Panel" user={user} portalColor="red">
      <div className="p-6 space-y-6" data-testid="wallet-management">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">V2 Wallet Management</h1>
            <p className="text-sm text-gray-500">Manage customer wallets, topup, and transactions</p>
          </div>
          <Button onClick={() => setShowCreate(true)} data-testid="create-wallet-btn"><Plus className="w-4 h-4 mr-1" /> Create Wallet</Button>
        </div>

        {/* Create Modal */}
        {showCreate && (
          <div className="border rounded-lg p-4 bg-blue-50 space-y-3">
            <h3 className="font-semibold">Create New Wallet</h3>
            <div className="grid grid-cols-3 gap-3">
              <select className="border rounded px-3 py-2 text-sm" value={selectedCustomer} onChange={e => setSelectedCustomer(e.target.value)} data-testid="wallet-customer-select">
                <option value="">Select Customer</option>
                {availableCustomers.map(c => <option key={c.id} value={c.id}>{c.name} ({c.email})</option>)}
              </select>
              <Input placeholder="Initial balance" value={initBalance} onChange={e => setInitBalance(e.target.value)} data-testid="wallet-init-balance" />
              <Input placeholder="Credit limit" value={creditLimit} onChange={e => setCreditLimit(e.target.value)} data-testid="wallet-credit-limit" />
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={createWallet} data-testid="wallet-create-submit">Create</Button>
              <Button size="sm" variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            </div>
          </div>
        )}

        {/* Topup Modal */}
        {showTopup && (
          <div className="border rounded-lg p-4 bg-green-50 space-y-3">
            <h3 className="font-semibold">Topup Wallet</h3>
            <div className="flex gap-3">
              <Input placeholder="Amount" type="number" value={topupAmount} onChange={e => setTopupAmount(e.target.value)} className="w-32" data-testid="topup-amount" />
              <Input placeholder="Description" value={topupDesc} onChange={e => setTopupDesc(e.target.value)} className="flex-1" />
              <Button size="sm" onClick={doTopup} data-testid="topup-submit">Topup</Button>
              <Button size="sm" variant="outline" onClick={() => setShowTopup(null)}>Cancel</Button>
            </div>
          </div>
        )}

        {/* Wallets Table */}
        <div className="bg-white border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600 uppercase text-xs">
              <tr>
                <th className="px-4 py-3 text-left">Customer</th>
                <th className="px-4 py-3">Balance</th>
                <th className="px-4 py-3">Hold</th>
                <th className="px-4 py-3">Available</th>
                <th className="px-4 py-3">Credit Limit</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {loading ? (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-400"><RefreshCw className="w-4 h-4 animate-spin inline mr-2" />Loading...</td></tr>
              ) : wallets.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-400"><Wallet className="w-5 h-5 inline mr-2" />No wallets created yet</td></tr>
              ) : wallets.map(w => (
                <tr key={w.id} className="hover:bg-gray-50" data-testid={`wallet-row-${w.customer_id}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium">{w.customer_name || "—"}</div>
                    <div className="text-xs text-gray-500">{w.customer_email}</div>
                  </td>
                  <td className="px-4 py-3 text-center font-bold text-gray-900">Rs.{w.balance?.toLocaleString()}</td>
                  <td className="px-4 py-3 text-center text-orange-600">Rs.{w.hold_amount?.toLocaleString()}</td>
                  <td className="px-4 py-3 text-center font-bold text-green-600">Rs.{w.available_balance?.toLocaleString()}</td>
                  <td className="px-4 py-3 text-center">Rs.{w.credit_limit?.toLocaleString()}</td>
                  <td className="px-4 py-3 text-center">{w.is_active ? <span className="text-green-600 text-xs font-medium">Active</span> : <span className="text-red-500 text-xs">Inactive</span>}</td>
                  <td className="px-4 py-3 text-right space-x-1">
                    <Button size="sm" variant="outline" onClick={() => { setShowTopup(w.customer_id); setTopupAmount(""); }} data-testid={`topup-btn-${w.customer_id}`}>
                      <ArrowUpCircle className="w-3 h-3 mr-1" /> Topup
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => viewTxns(w.customer_id)} data-testid={`txns-btn-${w.customer_id}`}>Txns</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Transactions Panel */}
        {showTxns && (
          <div className="border rounded-lg p-4" data-testid="txns-panel">
            <div className="flex justify-between items-center mb-3">
              <h3 className="font-semibold">Transaction History</h3>
              <Button size="sm" variant="outline" onClick={() => setShowTxns(null)}>Close</Button>
            </div>
            {txns.length === 0 ? <p className="text-sm text-gray-400">No transactions</p> : (
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-xs text-gray-600 uppercase">
                  <tr><th className="px-3 py-2 text-left">Date</th><th className="px-3 py-2 text-left">Type</th><th className="px-3 py-2">Amount</th><th className="px-3 py-2">Balance After</th><th className="px-3 py-2 text-left">Description</th></tr>
                </thead>
                <tbody className="divide-y">
                  {txns.map(t => (
                    <tr key={t.id}>
                      <td className="px-3 py-2 text-xs">{new Date(t.created_at).toLocaleString()}</td>
                      <td className="px-3 py-2">{t.type === "CREDIT" ? <span className="text-green-600 flex items-center gap-1"><ArrowUpCircle className="w-3 h-3" />Credit</span> : <span className="text-red-600 flex items-center gap-1"><ArrowDownCircle className="w-3 h-3" />Debit</span>}</td>
                      <td className="px-3 py-2 text-center font-medium">Rs.{t.amount}</td>
                      <td className="px-3 py-2 text-center">Rs.{t.balance_after}</td>
                      <td className="px-3 py-2 text-gray-500">{t.description}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
